<template>
  <div class="card">
    <div class="card-body">
      <h4 class="card-title">Orderpagina</h4>
      
      <div class="row p-0 m-0">
        <div class="col-lg-3 col-md-4 mt-2">
      
          <!-- PDF Upload Dropzone -->
          <PdfUploadDropzone v-if="canUsePdfParser"
            @pdfQueued="handlePdfQueued"
            @showToast="handleToast"
          />

          <!-- Gedeelde PDF-parse-wachtrij (zichtbaar voor admins + pdf_parser-rol) -->
          <PdfParseQueueList v-if="canUsePdfParser" :isAdmin="isAdmin" @showToast="handleToast" />

          <FilterOrder @filterStatusChanged="filterStatusChanged" :orderstatussen="orderstatussen" :isAdmin="isAdmin" />
        </div>
        <div class="col-lg-9 col-md-8 mt-2">
          <SearchOrder
              :searchQuery="searchQuery"
              placeholder="Zoeken op klantnaam, referentie of Verkoopordernummer"
              @performSearch="performSearch"
          />
          <div class="table-responsive">
            <table class="table caption-top table-striped border table-hover">
              <caption>* organisatienaam niet gevonden</caption>
              <thead>
              <tr>
                <th scope="col" 
                    tabindex="0"
                    role="button"
                    :aria-label="`Sort by status ${currentSort === 'status' ? (currentSortDir === 'asc' ? 'ascending' : 'descending') : ''}`"
                    @click="sort('status')" 
                    @keydown.enter="sort('status')"
                    @keydown.space.prevent="sort('status')"
                    :class="sortClass('status')">&nbsp;</th>
                <th scope="col" 
                    tabindex="0"
                    role="button"
                    :aria-label="`Sort by processed date ${currentSort === 'updated_at' ? (currentSortDir === 'asc' ? 'ascending' : 'descending') : ''}`"
                    @click="sort('updated_at')" 
                    @keydown.enter="sort('updated_at')"
                    @keydown.space.prevent="sort('updated_at')"
                    :class="sortClass('updated_at')" 
                    class="text-center">
                  Verwerkt
                </th>
                <th scope="col" 
                    tabindex="0"
                    role="button"
                    :aria-label="`Sort by customer ${currentSort === 'customer' ? (currentSortDir === 'asc' ? 'ascending' : 'descending') : ''}`"
                    @click="sort('customer')" 
                    @keydown.enter="sort('customer')"
                    @keydown.space.prevent="sort('customer')"
                    :class="sortClass('customer')">Klant</th>
                <th scope="col" 
                    tabindex="0"
                    role="button"
                    :aria-label="`Sort by reference ${currentSort === 'reference' ? (currentSortDir === 'asc' ? 'ascending' : 'descending') : ''}`"
                    @click="sort('reference')" 
                    @keydown.enter="sort('reference')"
                    @keydown.space.prevent="sort('reference')"
                    :class="sortClass('reference')">Reference</th>
                <th scope="col"
                    tabindex="0"
                    role="button"
                    :aria-label="`Sort by source ${currentSort === 'source' ? (currentSortDir === 'asc' ? 'ascending' : 'descending') : ''}`"
                    @click="sort('source')"
                    @keydown.enter="sort('source')"
                    @keydown.space.prevent="sort('source')"
                    :class="sortClass('source')"
                    class="text-center">Bron</th>
                <th scope="col"
                    tabindex="0"
                    role="button"
                    :aria-label="`Sort by sales order number ${currentSort === 'vo_nummer' ? (currentSortDir === 'asc' ? 'ascending' : 'descending') : ''}`"
                    @click="sort('vo_nummer')"
                    @keydown.enter="sort('vo_nummer')"
                    @keydown.space.prevent="sort('vo_nummer')"
                    :class="sortClass('vo_nummer')">Verkoopord.</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(order, index) in filteredOrders" :key="index"
                  class="clickable-row"
                  tabindex="0"
                  role="button"
                  :aria-label="`View order details for ${order.customer?.organisation_name || order.customer?.name || 'Unknown'}, reference ${order.reference}`"
                  @click="goToOrderDetailPage(index)"
                  @keydown.enter="goToOrderDetailPage(index)"
                  @keydown.space.prevent="goToOrderDetailPage(index)">
                <td>
                  <i class="bi bi-circle-fill close"
                     :class="getColor(order.status)"
                     :title="order.status?.label"
                     :aria-label="`Status: ${order.status?.label || 'Unknown'}`"
                     role="img"
                  ></i>
                </td>
                <td class="text-center">{{ getDateTime(order.updated_at) }}</td>
                <td>{{
                    order.customer?.organisation_name ? order.customer?.organisation_name : order.customer?.name + '*'
                  }}
                </td>
                <td>{{ order.reference }}</td>
                <td class="text-center">
                  <span
                    class="badge source-badge"
                    :class="getSourceInfo(order.customer?.username).badgeClass"
                    :title="getSourceInfo(order.customer?.username).fullName"
                  >{{ getSourceInfo(order.customer?.username).abbreviation }}</span>
                  <i
                    v-if="order.customer?.username === 'pdfparsing' && !order.vo_nummer"
                    class="bi bi-exclamation-triangle-fill text-warning ms-1"
                    title="Controleren en versturen naar Unit4 vereist"
                  ></i>
                  <i
                    v-if="order.user_agent === 'Claude-AI'"
                    class="bi bi-robot text-warning ms-1"
                    title="Aangemaakt door AI — nog niet gecontroleerd door een mens"
                  ></i>
                </td>
                <td>
                  <span v-if="order.vo_nummer">{{ order.vo_nummer }}</span>
                  <button
                    v-else
                    class="btn btn-sm btn-secondary"
                    @click.stop="checkVoNumber(order, index)"
                    :disabled="checkingVoNumber[order.id]"
                    title="Controleer of order al bestaat in Unit4"
                  >
                    <i v-if="checkingVoNumber[order.id]" class="bi bi-clock-fill"></i>
                    <i v-else class="bi bi-search"></i>
                    Check
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
            <div ref="sentinel"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import ServiceFactory from "../services/ServiceFactory"
import FilterOrder from "../components/FilterOrder.vue"
import Utils from "../utils/Utils"
import SearchOrder from "@/components/SearchOrder.vue";
import fetchOrdersAction from "@/services/FetchOrdersAction.js";
import NavigationService from "@/services/NavigationService.js";
import PdfUploadDropzone from "@/components/PdfUploadDropzone.vue";
import PdfParseQueueList from "@/components/PdfParseQueueList.vue";

export default {
  name: 'OrdersPage',
  props: ['user', 'isAdmin', 'canUsePdfParser'],
  components: {SearchOrder, FilterOrder, PdfUploadDropzone, PdfParseQueueList},
  data() {
    return {
      searchQuery: '',
      searchStatus: '',
      stateFilter: '',
      searchDataStart: '',
      searchDataEnd: '',
      searchCustomers: '',
      searchUserAgent: '',
      orderService: null,
      orders: [],
      orderstatussen: [],
      filteredOrders: [],
      currentSort: 'updated_at',
      currentSortDir: 'desc',
      webSocketService: null,
      page: 1,
      prevPage: 0,
      pageSize: 25,
      observer: null,
      currentFilter: null,
      checkingVoNumber: {}
    }
  },
  watch: {
    user(newVal) {
      if (newVal) {
        this.initializeOrdersAndWebSocket()
      }
    }
  },
  methods: {
    handlePdfQueued(result) {
      // PDF is in de gedeelde wachtrij geplaatst; de PdfParseQueueList toont de voortgang
      // en zodra de worker klaar is verschijnt de order via het 'ordersUpdated' event.
      console.log('PDF in wachtrij geplaatst:', result);
    },
    
    handleToast(toastData) {
      // Doorsturen naar parent component voor toast weergave
      this.$emit('showToast', toastData);
    },
    
    async checkVoNumber(order, index) {
      try {
        // Set loading state
        this.checkingVoNumber[order.id] = true;
        
        // Call the backend endpoint to check VO number
        const response = await this.orderService.checkVoNumber(order.id);
        
        if (response.success) {
          if (response.orderFound && response.voNumber) {
            // Order found in Unit4, update the order in the UI
            this.filteredOrders[index].vo_nummer = response.voNumber;
            // Also update status if available in the response
            if (response.updatedOrder && response.updatedOrder.status) {
              this.filteredOrders[index].status = response.updatedOrder.status;
            }
            this.$emit('showToast', ['Order gevonden in Unit4! VO nummer toegevoegd.', 'bg-success']);
          } else {
            this.$emit('showToast', ['Order niet gevonden in Unit4', 'bg-info']);
          }
        } else {
          this.$emit('showToast', [response.message || 'Fout bij controleren VO nummer', 'bg-danger']);
        }
      } catch (error) {
        console.error('Error checking VO number:', error);
        this.$emit('showToast', ['Fout bij controleren VO nummer', 'bg-danger']);
      } finally {
        // Remove loading state
        this.checkingVoNumber[order.id] = false;
      }
    },
    
    performSearch(query) {
      this.searchQuery = query;
      this.page = 1;
      this.prevPage = 0;
      this.loadOrdersForSearch();
      this.prevPage = this.page;
      this.page += 1;
    },
    getColor(status) {
      return Utils.getColorForOrderStatus(status)
    },
    sort(sortBy) {
      if (sortBy === this.currentSort) {
        this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSort = sortBy;
      }
      this.performSearch(this.searchQuery);
    },
    sortClass(column) {
      return {
        'sort-asc': this.currentSort === column && this.currentSortDir === 'asc',
        'sort-desc': this.currentSort === column && this.currentSortDir === 'desc'
      };
    },
    filterStatusChanged(filterObj) {
      let statuses = [];
      if (filterObj.status && typeof filterObj.status === 'object' && !Array.isArray(filterObj.status)) {
        statuses = Object.entries(filterObj.status)
            .filter(([, value]) => value === true)
            .map(([key]) => Number(key));
      }
      this.searchStatus = statuses.join(',')
      this.searchDataStart = filterObj.startDate;
      this.searchDataEnd = filterObj.endDate;

      let customers = [];
      if (filterObj.customer && typeof filterObj.customer === 'object' && !Array.isArray(filterObj.customer)) {
        customers = Object.entries(filterObj.customer)
            .filter(([, value]) => value === true)
            .map(([key]) => String(key));
      }
      this.searchCustomers = customers.join(',');
      this.searchUserAgent = filterObj.userAgentFilter;
      this.currentFilter = filterObj;

      this.performSearch(this.searchQuery);
    },
    getDateTime(isoString) {
      return Utils.formatDateTimeString(isoString)
    },
    getSourceInfo(username) {
      const sourceMap = {
        pdfparsing:       { abbreviation: 'AI',  fullName: 'PDF Parsing (AI)',              badgeClass: 'badge-source-ai' },
        spscommerce:      { abbreviation: 'SPS', fullName: 'SPS Commerce (EDI)',             badgeClass: 'badge-source-edi' },
        webshop:          { abbreviation: 'WEB', fullName: 'Webshop',                        badgeClass: 'badge-source-web' },
        bartrack:         { abbreviation: 'BTK', fullName: 'Bartrack',                       badgeClass: 'badge-source-default' },
        newtron:          { abbreviation: 'NEW', fullName: 'Newtron (JSON)',                 badgeClass: 'badge-source-default' },
        sabic:            { abbreviation: 'SAB', fullName: 'Sabic (JSON API)',               badgeClass: 'badge-source-default' },
        weir:             { abbreviation: 'WEI', fullName: 'Weir (Coupa cXML)',              badgeClass: 'badge-source-cxml' },
        cabot:            { abbreviation: 'CXM', fullName: 'Cabot (Coupa cXML)',             badgeClass: 'badge-source-cxml' },
        equans:           { abbreviation: 'CXM', fullName: 'Equans (Coupa cXML)',            badgeClass: 'badge-source-cxml' },
        mccain:           { abbreviation: 'CXM', fullName: 'McCain (Coupa cXML)',            badgeClass: 'badge-source-cxml' },
        avebe:            { abbreviation: 'AVB', fullName: 'Avebe (Coupa cXML)',             badgeClass: 'badge-source-cxml' },
        hadro:            { abbreviation: 'HDR', fullName: 'Hadro (JSON)',                   badgeClass: 'badge-source-default' },
        frieslandcampina: { abbreviation: 'FLC', fullName: 'FrieslandCampina (Coupa cXML)', badgeClass: 'badge-source-cxml' },
        sherwin:          { abbreviation: 'SHW', fullName: 'Sherwin-Williams (Coupa cXML)', badgeClass: 'badge-source-cxml' },
      };
      return sourceMap[username] ?? { abbreviation: '?', fullName: username || 'Onbekend', badgeClass: 'badge-source-default' };
    },
    goToOrderDetailPage(index) {
      // Sla de huidige lijst met gefilterde orders en de navigatiestatus op
      NavigationService.saveItems(
        this.filteredOrders, 
        {
          searchQuery: this.searchQuery,
          currentSort: this.currentSort,
          currentSortDir: this.currentSortDir,
          searchStatus: this.searchStatus,
          stateFilter: this.stateFilter,
          searchDataStart: this.searchDataStart,
          searchDataEnd: this.searchDataEnd,
          searchCustomers: this.searchCustomers,
          searchUserAgent: this.searchUserAgent
        },
        'order', // itemType
        'Orders'  // listRouteName
      );
      
      // Sla de huidige index op
      NavigationService.setCurrentIndex(index, 'order');
      
      // Navigeer naar de detailpagina
      this.$router.push({name: 'OrderDetail', params: {id: this.filteredOrders[index].id}});
    },
    async loadOrderstatussen() {
      this.orderstatussen = await this.orderService.getOrderstatussen();
    },
    async loadOrderList() {
      const newOrders = await fetchOrdersAction.getOrders(
          this.page,
          this.pageSize,
          this.currentSort,
          this.currentSortDir,
          this.searchQuery,
          this.searchDataStart,
          this.searchDataEnd,
          this.searchStatus,
          this.searchCustomers,
          this.searchUserAgent,
      );
      if (!newOrders) return;

      return [...newOrders]
    },
    async loadOrdersForSearch() {
      const newOrders = await this.loadOrderList();
      if (!newOrders) return;
      this.orders = newOrders
      this.filteredOrders = newOrders;
      
      // Sla de gefilterde orders op voor navigatie
      NavigationService.saveItems(this.filteredOrders, {
        searchQuery: this.searchQuery,
        currentSort: this.currentSort,
        currentSortDir: this.currentSortDir,
        searchStatus: this.searchStatus,
        stateFilter: this.stateFilter,
        searchDataStart: this.searchDataStart,
        searchDataEnd: this.searchDataEnd,
        searchCustomers: this.searchCustomers,
        searchUserAgent: this.searchUserAgent
      });
    },
    async loadOrders() {
      //prevent double loading of the same page
      if (this.prevPage === this.page) return;
      this.prevPage = this.page;

      const newOrders = await this.loadOrderList();
      if (!newOrders) return;

      this.orders = [...this.orders, ...newOrders];
      this.filteredOrders = this.orders;
      this.page += 1;
    },
    observeSentinel() {
      // Controleer eerst of de sentinel ref bestaat
      if (!this.$refs.sentinel) {
        console.log('Sentinel element niet gevonden, observer niet geïnitialiseerd');
        return;
      }

      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1.0
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadOrders();
          }
        });
      }, options);

      this.observer.observe(this.$refs.sentinel);
    },
    async initializeOrdersAndWebSocket() {
      await this.loadOrders()
      await this.loadOrderstatussen()
      this.webSocketService = ServiceFactory.getService('WebSocketService')

      this.webSocketService.addEventListener('ordersUpdated', (event) => {
        // Nieuwe orders ontvangen via WebSocket
        this.orders = event.detail;
        
        // Cache leegmaken zodat bij terugkeer naar OrdersPage de nieuwe orders worden opgehaald
        if (fetchOrdersAction) {
          fetchOrdersAction.clearCache();
        }
        
        // Filters correct toepassen op de nieuwe data
        this.$nextTick(() => {
          if (this.currentFilter) {
            this.filterStatusChanged(this.currentFilter);
          } else {
            this.filteredOrders = this.orders;
          }
        });
      });
      this.webSocketService.connect()
    },
  },
  async mounted() {
    this.orderService = ServiceFactory.getService('OrderService')
    this.orderService.addEventListener('getOrdersFailed', () => {
      this.$emit('showToast', ['Fout bij ophalen orders', 'bg-danger'])
    })

    if (this.user) {
      // Zorgen dat eventuele cache wordt geleegd bij (her)laden van de pagina
      if (fetchOrdersAction) {
        fetchOrdersAction.clearCache();
      }
      
      await this.initializeOrdersAndWebSocket()
    }
    this.observeSentinel();
  },
  
  // Opmerking: activated() is verwijderd omdat bij navigatie mounted() al wordt aangeroepen
  // en componenten volledig opnieuw worden geladen
  beforeUnmount() {
    // Cleanup IntersectionObserver
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Cleanup WebSocket event listeners
    if (this.webSocketService) {
      this.webSocketService.removeEventListener('ordersUpdated');
    }
  }
}
</script>

<style scoped>
.card {
  border: none;
}

th {
  cursor: pointer;
}

.sort-asc::after {
  content: ' ▲';
}

.sort-desc::after {
  content: ' ▼';
}

.source-badge {
  font-size: 0.7rem;
  letter-spacing: 0.03em;
  cursor: default;
}

.badge-source-ai {
  background-color: #6f42c1;
  color: #fff;
}

.badge-source-edi {
  background-color: #0d6efd;
  color: #fff;
}

.badge-source-web {
  background-color: #198754;
  color: #fff;
}

.badge-source-cxml {
  background-color: #fd7e14;
  color: #fff;
}

.badge-source-default {
  background-color: #6c757d;
  color: #fff;
}
</style>